import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { MainContent } from './MainContent';
import NewProjectModal from '../NewProjectModal';
import { LogOut } from 'lucide-react';
import { ProjectService } from '../../services/projectService';
import { StorageIndicator } from '../alpha/StorageIndicator';
import { isAlphaVersion } from '../../config/app.config';

export function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState("Recents");
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [currentView, setCurrentView] = useState("projects"); // "projects", "collections", or "files"
  const [isNewProjectModalOpen, setIsNewProjectModalOpen] = useState(false);
  const [collectionsCache, setCollectionsCache] = useState(new Map()); // Cache collection data including file counts
  const [shouldRefreshData, setShouldRefreshData] = useState(false); // Flag to trigger data refresh

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUserInfo(parsedUser);
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('user');
        navigate('/login');
      }
    } else {
      navigate('/login');
    }
  }, [navigate]);

  // Handle cached navigation state from Workspace back button
  useEffect(() => {
    const navigationState = location.state;
    
    if (navigationState?.restoreNavigation) {
      // Restore from cached state - no API calls needed!
      setSelectedProject(navigationState.selectedProject);
      setSelectedCollection(navigationState.selectedCollection);
      setCurrentView(navigationState.currentView);
      
      // Check if we need to refresh data (coming back from Workspace with changes)
      if (navigationState.refreshData) {
        setShouldRefreshData(true);
        // Reset the flag after a short delay to allow components to use it
        setTimeout(() => setShouldRefreshData(false), 100);
      }
      
      // Clean up the navigation state
      navigate('/dashboard', { replace: true });
    }
  }, [location.state, navigate]);

  const handleNewProject = () => {
    setIsNewProjectModalOpen(true);
  };

  const handleCloseProjectModal = () => {
    setIsNewProjectModalOpen(false);
  };

  const handleProjectCreated = (newProject) => {
    console.log('Project created:', newProject);
    // Optionally navigate to the new project or refresh the project list
    setSelectedProject(newProject);
    setCurrentView("collections");
    // The modal will close itself after success
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    navigate('/');
  };

  const handleSettingsClick = () => {
    // TODO: Implement settings dialog
    console.log('Settings clicked');
  };

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    setSelectedCollection(null);
    setCurrentView("collections");
  };

  const handleCollectionSelect = (collection) => {
    setSelectedCollection(collection);
    // Cache the collection data
    setCollectionsCache(prev => {
      const newCache = new Map(prev);
      newCache.set(collection._id, collection);
      return newCache;
    });
    setCurrentView("files");
  };

  const handleCollectionUpdated = (collectionId, updateType, data = {}) => {
    console.log('🔄 handleCollectionUpdated called:', { collectionId, updateType, data });

    if (updateType === 'fileDeleted') {
      // Update collection file counts when files are deleted
      setCollectionsCache(prev => {
        const newCache = new Map(prev);
        const collection = newCache.get(collectionId);
        if (collection && collection.fileCount > 0) {
          // Decrement the file count
          newCache.set(collectionId, {
            ...collection,
            fileCount: collection.fileCount - 1
          });
        }
        return newCache;
      });

      // Update the selected collection if it's the one being updated
      if (selectedCollection && selectedCollection._id === collectionId) {
        setSelectedCollection(prev => ({
          ...prev,
          fileCount: Math.max(0, (prev.fileCount || 0) - 1)
        }));
      }
    }
    else if (updateType === 'fileUploaded') {
      // Update collection file counts when files are uploaded
      setCollectionsCache(prev => {
        const newCache = new Map(prev);
        const collection = newCache.get(collectionId);
        if (collection) {
          // Increment the file count
          newCache.set(collectionId, {
            ...collection,
            fileCount: (collection.fileCount || 0) + 1
          });
        }
        return newCache;
      });

      // Update the selected collection if it's the one being updated
      if (selectedCollection && selectedCollection._id === collectionId) {
        setSelectedCollection(prev => ({
          ...prev,
          fileCount: (prev.fileCount || 0) + 1
        }));
      }
    }
    else if (updateType === 'collectionCreated') {
      // Add newly created collection to cache
      if (data.collection) {
        setCollectionsCache(prev => {
          const newCache = new Map(prev);
          newCache.set(data.collection._id, data.collection);
          return newCache;
        });
      }
    }
    else if (updateType === 'collectionDeleted') {
      // Remove deleted collection from cache
      setCollectionsCache(prev => {
        const newCache = new Map(prev);
        newCache.delete(collectionId);
        return newCache;
      });
    }
  };

  const handleBackToProjects = () => {
    setSelectedProject(null);
    setSelectedCollection(null);
    setCurrentView("projects");
  };

  const handleBackToCollections = () => {
    setSelectedCollection(null);
    setCurrentView("collections");
  };

  const handleFileSelect = (file) => {
    // Navigate to workspace with the file, including full navigation context
    navigate(`/project/${selectedProject._id}/file/${file._id}`, {
      state: {
        backTo: 'collection',
        // Pass the actual objects instead of just IDs to avoid API calls
        project: selectedProject,
        collection: selectedCollection,
        currentView: currentView
      }
    });
  };

  if (!userInfo) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-950">
      {/* Header Bar */}
      <div className="fixed top-0 left-0 right-0 h-16 bg-[#2a282a] backdrop-blur-md border-b border-[#535050] z-50 flex items-center justify-between px-6">
        <div className="flex items-center gap-4">
          <div className="text-2xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
            DigiTables
          </div>
          {isAlphaVersion() && (
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              ALPHA
            </div>
          )}
        </div>
        <div className="flex items-center gap-4">
          {/* Storage indicator for alpha users */}
          {isAlphaVersion() && userInfo && (
            <div className="hidden md:block">
              <StorageIndicator userInfo={userInfo} className="bg-transparent border-0 p-2" />
            </div>
          )}
          <div className="relative">
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="focus:outline-none"
          >
            {userInfo?.picture ? (
              <img 
                src={userInfo.picture}
                alt="Profile"
                className="h-8 w-8 rounded-full object-cover ring-2 ring-transparent hover:ring-gray-200 transition-all"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="h-8 w-8 bg-slate-700 rounded-full flex items-center justify-center text-slate-200 text-sm">
                {userInfo.name?.[0] || 'U'}
              </div>
            )}
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-[#2a282a] rounded-lg shadow-lg border border-[#494646] py-1 z-50">
              {userInfo && (
                <div className="px-4 py-2 border-b border-slate-700">
                  <p className="text-sm font-medium text-slate-100">{userInfo.name}</p>
                  <p className="text-xs text-slate-400">{userInfo.email}</p>
                </div>
              )}
              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 flex items-center gap-2"
              >
                <LogOut size={16} />
                Sign out
              </button>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Main Layout */}
      <div className="flex w-full mt-16">
        <Sidebar
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          selectedProject={selectedProject}
          selectedCollection={selectedCollection}
          setSelectedProject={setSelectedProject}
          onProjectSelect={handleProjectSelect}
          onBackToProjects={handleBackToProjects}
          onCollectionSelect={handleCollectionSelect}
          userInfo={userInfo}
        />
        <MainContent
          activeSection={activeSection}
          selectedProject={selectedProject}
          selectedCollection={selectedCollection}
          currentView={currentView}
          onNewProject={handleNewProject}
          onSettingsClick={handleSettingsClick}
          onProjectSelect={handleProjectSelect}
          onCollectionSelect={handleCollectionSelect}
          onBackToProjects={handleBackToProjects}
          onBackToCollections={handleBackToCollections}
          onFileSelect={handleFileSelect}
          userInfo={userInfo}
          onCollectionUpdated={handleCollectionUpdated}
          collectionsCache={collectionsCache}
          shouldRefreshData={shouldRefreshData}
        />
      </div>

      {/* Click outside handler for dropdown */}
      {isDropdownOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsDropdownOpen(false)}
        />
      )}

      {/* New Project Modal */}
      <NewProjectModal
        isOpen={isNewProjectModalOpen}
        onClose={handleCloseProjectModal}
        userInfo={userInfo}
        onProjectCreated={handleProjectCreated}
      />
    </div>
  );
}